/**
 * Platform operations port — web and mobile adapters implement this facade
 * so planner tools never import app-specific Firestore modules.
 */

import type {
  BudgetItem,
  DayPlanEvent,
  Guest,
  Wedding,
  PlannerMemory,
  PlannerMemoryNote,
  SeatingCanvasInset,
  Table,
  TableShape,
  Task,
  WebsiteConfig,
} from "@amazing-wedding/shared-types";
import type { AddNoteInput } from "./memory/operations";

export type GuestDraft = Omit<
  Guest,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "deletedAt"
  | "rsvpRespondedAt"
  | "guestCount"
  | "rsvpToken"
  | "rsvpSubmittedToken"
>;

export type GuestPatch = Partial<Omit<Guest, "id" | "createdAt" | "updatedAt" | "deletedAt">>;

export type TaskDraft = Omit<Task, "id" | "createdAt" | "updatedAt" | "completedAt">;
export type TaskPatch = Partial<Omit<Task, "id" | "createdAt" | "updatedAt">>;

export type BudgetItemDraft = Omit<BudgetItem, "id" | "createdAt" | "updatedAt">;
export type BudgetItemPatch = Partial<BudgetItemDraft>;

export type DayPlanEventDraft = Omit<DayPlanEvent, "id" | "createdAt" | "updatedAt">;
export type DayPlanEventPatch = Partial<DayPlanEventDraft>;

export interface TableDraft {
  name: string;
  shape: TableShape;
  capacity: number;
  positionX: number;
  positionY: number;
  rotation?: number;
  isVip?: boolean;
  color?: string | null;
  notes?: string | null;
  hiddenSeats?: number[];
}

export type TablePatch = Partial<Omit<Table, "id" | "createdAt" | "updatedAt" | "guestIds">> & {
  seatMap?: (string | null)[];
};

export interface SetupSeatingPlanLabels {
  vipTable: string;
  tableName: (index: number) => string;
}

export interface SetupSeatingPlanOptions {
  capacity?: number;
  tableCount?: number;
  shape?: TableShape;
  includeVipTable?: boolean;
  assignGuests?: boolean;
  keepGroupsTogether?: boolean;
  leaveSpareSeats?: boolean;
  labels?: SetupSeatingPlanLabels;
  resetAssignments?: boolean;
  canvasInset?: SeatingCanvasInset;
  roomWidthM?: number;
}

export interface SetupSeatingPlanResult {
  tablesCreated: number;
  guestsAssigned: number;
  unassignedGuestIds: string[];
  totalHeads: number;
  roomExpanded?: boolean;
}

export class PlanRequiredError extends Error {
  constructor(message = "Premium wymagany") {
    super(message);
    this.name = "PlanRequiredError";
  }
}

export interface PlannerOperations {
  guests: {
    createGuest: (weddingId: string, draft: GuestDraft) => Promise<string>;
    updateGuest: (
      weddingId: string,
      guestId: string,
      patch: GuestPatch,
      allGuests?: Guest[],
    ) => Promise<void>;
    softDeleteGuest: (weddingId: string, guestId: string, allGuests?: Guest[]) => Promise<void>;
    projectCreatedGuest: (
      id: string,
      draft: Pick<
        Guest,
        | "firstName"
        | "lastName"
        | "plusOne"
        | "children"
        | "plusOneAllowed"
        | "weddingRole"
        | "deletedAt"
      >,
    ) => Guest;
    syncAttendeeCount: (weddingId: string, linked?: Guest[] | null) => Promise<void>;
  };
  tasks: {
    createTask: (weddingId: string, draft: TaskDraft) => Promise<string>;
    updateTask: (weddingId: string, taskId: string, patch: TaskPatch) => Promise<void>;
    deleteTask: (weddingId: string, taskId: string) => Promise<void>;
  };
  budget: {
    addBudgetItem: (weddingId: string, input: BudgetItemDraft) => Promise<string>;
    updateBudgetItem: (weddingId: string, id: string, patch: BudgetItemPatch) => Promise<void>;
    deleteBudgetItem: (weddingId: string, id: string) => Promise<void>;
  };
  dayPlan: {
    createDayPlanEvent: (weddingId: string, draft: DayPlanEventDraft) => Promise<string>;
    updateDayPlanEvent: (
      weddingId: string,
      eventId: string,
      patch: DayPlanEventPatch,
    ) => Promise<void>;
    deleteDayPlanEvent: (weddingId: string, eventId: string) => Promise<void>;
  };
  seating: {
    assignGuestToSeat: (
      weddingId: string,
      planId: string,
      guestId: string,
      guest: Guest,
      targetTableId: string,
      targetSeatIndex: number,
      targetTable: Table,
      currentTableId: string | null,
      currentTable: Table | null,
      guestsById: Record<string, Guest>,
      allGuests: Guest[],
      allTableIds: string[],
    ) => Promise<void>;
    createTable: (weddingId: string, planId: string, draft: TableDraft) => Promise<string>;
    deleteTable: (
      weddingId: string,
      planId: string,
      tableId: string,
      seatedGuestIds: string[],
    ) => Promise<void>;
    moveGuestWithinTable: (
      weddingId: string,
      planId: string,
      guestId: string,
      guest: Guest,
      tableId: string,
      table: Table,
      sourceSeatIndex: number,
      targetSeatIndex: number,
      guestsById: Record<string, Guest>,
    ) => Promise<void>;
    unassignGuestWithSeatMap: (
      weddingId: string,
      planId: string,
      guestId: string,
      currentTableId: string,
      currentTable: Table,
      guestsById: Record<string, Guest>,
      allTableIds: string[],
    ) => Promise<void>;
    updateTable: (
      weddingId: string,
      planId: string,
      tableId: string,
      patch: TablePatch,
    ) => Promise<void>;
    setupSeatingPlan: (
      weddingId: string,
      planId: string,
      guests: Guest[],
      legacyPlanId: string | null | undefined,
      options?: SetupSeatingPlanOptions,
    ) => Promise<SetupSeatingPlanResult>;
  };
  memory: {
    loadPlannerMemory: (weddingId: string) => Promise<PlannerMemory>;
    addMemoryNote: (weddingId: string, input: AddNoteInput) => Promise<PlannerMemoryNote>;
    removeMemoryNote: (weddingId: string, noteId: string) => Promise<boolean>;
  };
  website: {
    updateWebsiteConfig: (weddingId: string, patch: Partial<WebsiteConfig>) => Promise<void>;
    PlanRequiredError: typeof PlanRequiredError;
  };
  rsvp: {
    ensureRsvpLink: (wedding: Wedding, guest: Guest) => Promise<string>;
    rsvpLinkUrl: (token: string) => string;
  };
}
